import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _isEqual from 'lodash/isEqual';

import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';
import SearchWithFilters from 'components/SearchWithFilters';
import ClientTable from './ClientTable';

import { usePrevious } from 'hooks';
import { clientsTable as TEXT } from 'locales/en/client';
import {
  clientAllSelector,
  setParams,
  getClients,
  searchClients,
  setSearchTerm,
  resetSearch,
} from 'redux/slices/client';
import Button from 'components/Button';

import { sortClientResults } from './helpers';

const Clients = () => {
  const dispatch = useDispatch();
  const { data, params, pagination, isLoading, searchTerm } = useSelector(
    (state) => clientAllSelector(state)
  );
  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchTerm = usePrevious(searchTerm);

  useEffect(() => {
    dispatch(getClients());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedData = useMemo(() => sortClientResults(data), [data]);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getClients());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchTerm && searchTerm === '')
    ) {
      dispatch(getClients());
    }
  }, [dispatch, params, prevParams, prevSearchTerm, searchTerm]);

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
        page: 1,
      })
    );
    dispatch(getClients());
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
    dispatch(getClients());
  };

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onClearInput = () => {
    dispatch(resetSearch());
  };

  const onSearch = () => dispatch(searchClients());

  return (
    <DashboardContainer
      header={TEXT.header}
      rightContent={
        <div>
          <Button
            className="btn-secondary h-10 w-40 p-0 text-sm"
            title={TEXT.createClient}
          />
        </div>
      }
    >
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeInput={onChangeSearchTerm}
        searchTerm={searchTerm}
        onSearch={onSearch}
        placeholder={TEXT.search}
        onClearInput={onClearInput}
      />
      <ClientTable
        data={sortedData}
        pagination={pagination}
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      />
    </DashboardContainer>
  );
};

export default Clients;
